import AppData from 'Data/AppData';
import { auth_mode, OBJECT_STATUS, user_roles } from 'Data/Enum';

export const downloadBlob = (res, filename) => {
  let blob = new Blob([res.text], { type: 'text/csv' });
  if (window.navigator?.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename);
  } else {
    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  }
};

export const formatBytes = (a, b = 2) => {
  if (!+a) return '0 Bytes';
  const c = 0 > b ? 0 : b,
    d = Math.floor(Math.log(a) / Math.log(1024));
  return `${parseFloat((a / Math.pow(1024, d)).toFixed(c))}${
    ['B', 'KB', 'MB', 'GB'][d]
  }`;
};

export const formatMoney = (amount) => {
  if (isNaN(Number(amount))) {
    return '';
  } else {
    return Number(amount)
      .toFixed(2)
      .replace(/\d(?=(\d{3})+\.)/g, '$&,');
  }
};

export const roundToHundredth = (amount) => {
  if (isNaN(Number(amount))) {
    return 0;
  } else {
    return Math.round(Number(amount) * 100) / 100;
  }
};

export const getClickableLink = (link) => {
  let url = link?.trim();
  return url?.startsWith('http') ? url.trim() : `//${url}`;
};

export const areEquivalent = (a, b) => {
  if (a === b || a == b) {
    return true;
  }

  let isNumberRegex = /^[-+]?[0-9]+\.?[0-9]*$/;

  if (a instanceof Date || b instanceof Date) {
    let aDate = a instanceof Date ? a : new Date(a);
    let bDate = b instanceof Date ? b : new Date(b);
    return aDate == bDate;
  } else if (Array.isArray(a) || Array.isArray(b)) {
    return a?.length === b?.length && JSON.stringify(a) === JSON.stringify(b);
  } else if (isNumberRegex.test(a) || isNumberRegex.test(b)) {
    return (
      isNumberRegex.test(a) &&
      isNumberRegex.test(b) &&
      parseFloat(a) === parseFloat(b)
    );
  } else if (typeof a === 'object' && typeof b === 'object') {
    // Create arrays of property names
    let aProps = Object.getOwnPropertyNames(a);
    let bProps = Object.getOwnPropertyNames(b);

    // If number of properties is different,
    // objects are not equivalent
    if (aProps.length !== bProps.length) {
      return false;
    }

    for (const propName of aProps) {
      // If values of same property are not equal,
      // objects are not equivalent
      if (!areEquivalent(a[propName], b[propName])) {
        return false;
      }
    }

    // If we made it this far, objects
    // are considered equivalent
    return true;
  }
};

export const groupToMap = (initialArray, groupByField) => {
  return initialArray.reduce(
    (entryMap, e) =>
      entryMap.set(e[groupByField], [
        ...(entryMap.get(e[groupByField]) || []),
        e,
      ]),
    new Map(),
  );
};

const doesHaveOrgRole = (requestedOrgRole, userOrgRoles = []) => {
  if (!Array.isArray(userOrgRoles) || userOrgRoles == null) {
    userOrgRoles = [];
  }
  if (!Array.isArray(requestedOrgRole) || requestedOrgRole.length !== 2) {
    return false;
  }

  let requestedRoleName = requestedOrgRole[1].key || requestedOrgRole[1];

  if (requestedOrgRole[0] === '*') {
    return userOrgRoles
      .map((role) => role.split('::')[1])
      .includes(requestedRoleName);
  } else {
    return userOrgRoles.includes(
      `${requestedOrgRole[0]}::${requestedRoleName}`,
    );
  }
};

const doesHaveAllRoles = (role_list, orgRoles, userRoles) => {
  return role_list.reduce((hasAllRoles, r) => {
    if (Array.isArray(r)) {
      if (!doesHaveOrgRole(r, orgRoles)) {
        return false;
      }
    } else {
      if (!userRoles.includes(r)) {
        return false;
      }
    }
    return hasAllRoles;
  }, true);
};

const doesHaveAnyRoles = (role_list, orgRoles, userRoles) => {
  return role_list.reduce((hasOneRole, r) => {
    if (Array.isArray(r)) {
      if (doesHaveOrgRole(r, orgRoles)) {
        hasOneRole = true;
      }
    } else {
      if (userRoles.includes(r)) {
        hasOneRole = true;
      }
    }
    return hasOneRole;
  }, false);
};

export const isAuthorized = (requiredAuth, orgRoles, userRoles) => {
  if (!Array.isArray(userRoles)) {
    userRoles = [];
  }
  if (!Array.isArray(requiredAuth?.roles)) {
    requiredAuth.roles = [];
  }
  let role_list = requiredAuth.roles.map((user_role) => {
    if (Array.isArray(user_role)) {
      return user_role;
    } else {
      return user_role.key || user_role;
    }
  });

  // Check if user has ANY one of the required roles
  if (!requiredAuth?.mode || requiredAuth.mode === auth_mode.ANY) {
    return doesHaveAnyRoles(role_list, orgRoles, userRoles);
  }

  // Check if user has ALL requested roles
  if (requiredAuth.mode === auth_mode.ALL) {
    return doesHaveAllRoles(role_list, orgRoles, userRoles);
  }
};

export const isUserAuthorized = (requiredAuth) => {
  return isAuthorized(
    requiredAuth,
    AppData.organizationRoles(),
    AppData.userRoles(),
  );
};

export const canUserViewAllCores = () => {
  return isUserAuthorized({
    roles: [
      user_roles.ADMINISTRATOR,
      user_roles.ADMINISTRATIVE_APPROVER,
      user_roles.PERMISSIONS_ADMINISTRATOR,
      user_roles.RECONCILER,
      user_roles.SC_ADMIN,
    ],
  });
};

export const getGrandTotalForTransaction = (
  lineItemList,
  shippingItemAmount,
) => {
  return (
    lineItemList &&
    lineItemList.reduce((acc, lineItem) => {
      if (lineItem.unit_price && lineItem.quantity) {
        return acc + lineItem.unit_price * lineItem.quantity;
      } else {
        return acc;
      }
    }, +shippingItemAmount || 0)
  );
};

export const getNameWithInactiveTag = (object, nameField = 'name') => {
  return `${object[nameField]}${
    object.status === OBJECT_STATUS.DEACTIVATED.key
      ? ' (' + OBJECT_STATUS.DEACTIVATED.label + ')'
      : ''
  }`;
};

export const isItemInactive = (objectDisplayName) => {
  return objectDisplayName?.endsWith(
    '(' + OBJECT_STATUS.DEACTIVATED.label + ')',
  );
};
export const areSetsEqual = (setA, setB) =>
  setA.size === setB.size && [...setA].every((value) => setB.has(value));
